import {
    useEffect,
    useMemo,
    useRef,
    useState,
    type FC,
    type RefObject,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

/** 並び替え選択状態を示すチェックアイコン */
const CheckIcon: FC<{ className?: string }> = ({ className }) => (
    <svg
        viewBox="0 0 16 16"
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M3 8.5 L6.5 12 L13 4" />
    </svg>
);

/**
 * ColumnFilterPopup - テーブルヘッダー用 並び替え + 絞り込み ポップアップ
 *
 * 使い方:
 * <ColumnFilterPopup
 *     isOpen={open}
 *     onClose={() => setOpen(false)}
 *     anchorRef={anchorRef}
 *     sortDirection={sortDir}
 *     onSortChange={setSortDir}
 *     options={distinctValues.map(v => ({ value: v, label: v }))}
 *     selectedValues={selected}
 *     onSelectedValuesChange={setSelected}
 * />
 */

export type SortDirection = "asc" | "desc" | null;

export interface ColumnFilterOption {
    value: string;
    label: string;
}

export interface ColumnFilterPopupProps {
    isOpen: boolean;
    onClose: () => void;
    /** アンカー要素（フィルターアイコンのボタン）のRef */
    anchorRef: RefObject<HTMLElement | null>;
    /** 現在のソート方向 */
    sortDirection: SortDirection;
    /**
     * ソート方向の変更ハンドラ。
     * 同じ方向のボタンを再クリックした場合は null（解除）が渡される。
     */
    onSortChange: (direction: SortDirection) => void;
    /** チェックボックスに表示する選択肢（レコードから抽出した distinct 値） */
    options: ColumnFilterOption[];
    /** 現在選択中の値（空配列 = 絞り込みなし＝全件表示） */
    selectedValues: string[];
    /** 選択値の変更ハンドラ */
    onSelectedValuesChange: (values: string[]) => void;
}

const POPUP_WIDTH = 240;
const POPUP_MIN_HEIGHT = 200;
const GAP = 4;
const PAD = 8;

export const ColumnFilterPopup: FC<ColumnFilterPopupProps> = ({
    isOpen,
    onClose,
    anchorRef,
    sortDirection,
    onSortChange,
    options,
    selectedValues,
    onSelectedValuesChange,
}) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const selectAllRef = useRef<HTMLInputElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [positioned, setPositioned] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    // ポップアップを開くたびに検索クエリをリセット、閉じたらpositionedも初期化
    useEffect(() => {
        if (isOpen) {
            setSearchQuery("");
        } else {
            setPositioned(false);
        }
    }, [isOpen]);

    // --- アンカー位置計算 ---
    // 対象カラムを覆わないよう、ヘッダー右端を起点に開く（下に流す）。
    // 右側に入りきらない場合のみ左側へフォールバックする。
    useEffect(() => {
        if (!isOpen || !anchorRef.current) return;

        const updatePosition = () => {
            const anchor = anchorRef.current;
            if (!anchor) return;
            const rect = anchor.getBoundingClientRect();

            const popupEl = popupRef.current;
            const ph = popupEl?.offsetHeight ?? POPUP_MIN_HEIGHT;

            let top = rect.bottom + GAP;
            // ヘッダー右端から開く（カラム本体を隠さない）
            let left = rect.right + GAP;

            // 右に入りきらないなら、左側（カラムの左隣）へフォールバック
            if (left + POPUP_WIDTH > window.innerWidth - PAD) {
                const leftSide = rect.left - GAP - POPUP_WIDTH;
                left = leftSide >= PAD
                    ? leftSide
                    : Math.max(PAD, window.innerWidth - POPUP_WIDTH - PAD);
            }

            // 下に入りきらず、上に余裕があれば上に出す
            const fitsBelow = top + ph <= window.innerHeight - PAD;
            const fitsAbove = rect.top - GAP - ph >= PAD;
            if (!fitsBelow && fitsAbove) {
                top = rect.top - ph - GAP;
            }

            setPosition({ top, left });
            setPositioned(true);
        };

        requestAnimationFrame(updatePosition);
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);

        return () => {
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [isOpen, anchorRef, options.length]);

    // --- 外側クリックで閉じる ---
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node | null;
            if (!target) return;
            if (popupRef.current?.contains(target)) return;
            if (anchorRef.current?.contains(target)) return;
            onClose();
        };

        const timer = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose, anchorRef]);

    // --- ESCで閉じる ---
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [isOpen, onClose]);

    const filteredOptions = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, searchQuery]);

    const handleSortClick = (direction: Exclude<SortDirection, null>) => {
        // 再クリックで解除
        onSortChange(sortDirection === direction ? null : direction);
    };

    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    const handleCheckboxChange = (value: string, checked: boolean) => {
        if (checked) {
            onSelectedValuesChange([...selectedValues, value]);
        } else {
            onSelectedValuesChange(selectedValues.filter((v) => v !== value));
        }
    };

    // 全選択チェックボックスの状態（検索で絞り込まれた表示中の項目に対して判定）
    const allVisibleSelected =
        filteredOptions.length > 0 && filteredOptions.every((o) => selectedSet.has(o.value));
    const someVisibleSelected =
        filteredOptions.some((o) => selectedSet.has(o.value));

    // 3状態表示（indeterminate はDOM属性なので副作用で反映）
    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate =
                someVisibleSelected && !allVisibleSelected;
        }
    }, [someVisibleSelected, allVisibleSelected]);

    const handleSelectAllToggle = () => {
        if (filteredOptions.length === 0) return;
        const visibleSet = new Set(filteredOptions.map((o) => o.value));
        if (allVisibleSelected) {
            // 表示中の項目を選択から外す（検索外の選択は保持）
            onSelectedValuesChange(selectedValues.filter((v) => !visibleSet.has(v)));
        } else {
            // 表示中の項目を選択に加える
            const merged = new Set(selectedValues);
            visibleSet.forEach((v) => merged.add(v));
            onSelectedValuesChange(Array.from(merged));
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            ref={popupRef}
            role="dialog"
            className="fixed z-[9999] flex flex-col rounded-md border border-gray-200 bg-white shadow-lg"
            style={{
                top: position.top,
                left: position.left,
                width: POPUP_WIDTH,
                visibility: positioned ? "visible" : "hidden",
            }}
        >
            {/* 並び替え（左にチェック: 選択中はブランドカラー、未選択はグレー） */}
            <button
                type="button"
                onClick={() => handleSortClick("asc")}
                className="flex items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 border-b border-gray-200"
            >
                <CheckIcon
                    className={clsx(
                        "h-4 w-4 shrink-0",
                        sortDirection === "asc" ? "text-brand-500" : "text-gray-300",
                    )}
                />
                <span>昇順</span>
            </button>
            <button
                type="button"
                onClick={() => handleSortClick("desc")}
                className="flex items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-800 hover:bg-gray-50 border-b border-gray-200"
            >
                <CheckIcon
                    className={clsx(
                        "h-4 w-4 shrink-0",
                        sortDirection === "desc" ? "text-brand-500" : "text-gray-300",
                    )}
                />
                <span>降順</span>
            </button>

            {/* 検索（「検索」ラベル＋入力欄、placeholder なし） */}
            <div className="px-3 pt-3 pb-2">
                <div className="flex items-center gap-2">
                    <span className="shrink-0 text-sm text-gray-700">検索</span>
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={clsx(
                            "min-w-0 flex-1 rounded-md border border-gray-300 bg-white",
                            "px-2 py-1.5 text-sm text-gray-700",
                            "hover:border-gray-400",
                            "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
                        )}
                    />
                </div>
            </div>

            {/* チェックボックス一覧（先頭に「全選択」、多い時はスクロール） */}
            <div className="px-3 pb-3 max-h-64 overflow-auto">
                {/* 全選択（sticky で常時表示） */}
                <label
                    className={clsx(
                        "sticky top-0 z-10 bg-white",
                        "flex items-center gap-2 py-1.5 text-sm text-gray-800",
                        "border-b border-gray-100",
                        filteredOptions.length === 0 ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                    )}
                >
                    <input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allVisibleSelected}
                        disabled={filteredOptions.length === 0}
                        onChange={handleSelectAllToggle}
                        className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                    />
                    <span className="truncate">全選択</span>
                </label>

                {filteredOptions.length === 0 ? (
                    <div className="py-2 text-center text-xs text-gray-400">候補がありません</div>
                ) : (
                    <ul className="flex flex-col">
                        {filteredOptions.map((opt) => {
                            const checked = selectedSet.has(opt.value);
                            return (
                                <li key={opt.value}>
                                    <label className="flex items-center gap-2 py-1.5 cursor-pointer text-sm text-gray-800">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) =>
                                                handleCheckboxChange(opt.value, e.target.checked)
                                            }
                                            className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                                        />
                                        <span className="truncate">{opt.label}</span>
                                    </label>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default ColumnFilterPopup;
